import React, { useState } from 'react';
import JSZip from 'jszip'; // Import JSZip for zipping files
import { readAndCompressImage } from 'browser-image-resizer'; // Import browser-image-resizer
import ReCAPTCHA from 'react-google-recaptcha'; // Import reCAPTCHA
import './Upload.css';

// Configuration for resizing images
const imageResizeConfig = {
  quality: 0.8, // Image quality (0.1 to 1)
  maxWidth: 2048, // Maximum width
  maxHeight: 2048, // Maximum height
  autoRotate: true, // Automatically rotate based on EXIF data
};

const MAX_UPLOAD_SIZE = 20 * 1024 * 1024 * 1024; // 20GB in bytes

const Upload = () => {
  const [email, setEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [otp, setOtp] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [coverPhotos, setCoverPhotos] = useState([]); // State for cover photos
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [zippingProgress, setZippingProgress] = useState(0); // Zipping progress
  const [uploadProgress, setUploadProgress] = useState(0); // Upload progress
  const [statusMessage, setStatusMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false); // Spinner state
  const [uploadCompleted, setUploadCompleted] = useState(false); // Upload completion state
  const [captchaToken, setCaptchaToken] = useState(null); // State for reCAPTCHA token
  const [totalFileSize, setTotalFileSize] = useState(0); // State for total file size
  const [exceedsLimit, setExceedsLimit] = useState(false); // State for exceeding limit
  const [termsAccepted, setTermsAccepted] = useState(false); // State for terms acceptance

  const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

  const handleEmailChange = (e) => setEmail(e.target.value);
  const handleSenderNameChange = (e) => setSenderName(e.target.value);
  const handleOtpChange = (e) => setOtp(e.target.value);
  const handleTitleChange = (e) => setTitle(e.target.value);
  const handleMessageChange = (e) => setMessage(e.target.value);
  const handleFileChange = (e) => {
    const selectedFiles = [...e.target.files];
    const totalSize = selectedFiles.reduce((acc, file) => acc + file.size, 0);

    setTotalFileSize(totalSize);

    if (totalSize > MAX_UPLOAD_SIZE) {
      setExceedsLimit(true);
      setFiles([]); // Clear files if the limit is exceeded
    } else {
      setExceedsLimit(false);
      setFiles(selectedFiles);
    }
  };
  const handleCoverPhotoChange = (e) => {
    const selectedFiles = [...e.target.files];
    const jpgFiles = selectedFiles.filter((file) => file.type === 'image/jpeg'); // Allow only JPG files

    if (jpgFiles.length !== selectedFiles.length) {
      setStatusMessage('Only JPG files are allowed for cover photos.');
      setCoverPhotos([]); // Reject all files if any non-JPG file is selected
      e.target.value = ''; // Clear the file input
    } else {
      setStatusMessage(''); // Clear the message if all files are valid
      setCoverPhotos(jpgFiles);
    }
  }; // Handle cover photo selection
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token); // Update the reCAPTCHA token
  };
  const handleTermsChange = (e) => setTermsAccepted(e.target.checked); // Handle terms checkbox change

  const sendOtp = async () => {
    // Validation for mandatory fields
    if (!email.trim()) {
      setStatusMessage('Email is required.');
      return;
    }

    if (exceedsLimit) {
      setStatusMessage('The total upload size exceeds the 20GB limit.');
      return;
    }

    if (files.length === 0) {
      setStatusMessage('Please upload at least one file.');
      return;
    }

    if (!captchaToken) {
      setStatusMessage('Please complete the CAPTCHA to proceed.');
      return;
    }

    try {
      const response = await fetch(`${SERVER_URL}/upload/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, senderName, captchaToken }), // Include the reCAPTCHA token
      });

      if (!response.ok) throw new Error('Failed to send OTP');
      setStep(2);
      setStatusMessage('OTP sent to your email.');
    } catch (error) {
      console.error('Error sending OTP:', error);
      setStatusMessage('Error sending OTP.');
    }
  };

  const verifyOtp = async () => {
    try {
      const response = await fetch(`${SERVER_URL}/upload/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });

      if (!response.ok) throw new Error('Invalid OTP');
      setStep(3);
      setStatusMessage('OTP verified. Preparing to upload files.');
      uploadFiles(); // Automatically start the upload after OTP verification
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setStatusMessage('Error verifying OTP.');
    }
  };

  const uploadFiles = async () => {
    setUploading(true);
    setZippingProgress(0);
    setUploadProgress(0);
    setIsProcessing(true); // Show spinner
    setStatusMessage('Resizing and uploading cover photos...');

    try {
      // Step 1: Resize and upload cover photos individually
      const coverPhotoUrls = [];
      const uploadDate = Date.now();
      const safeEmail = email.replace(/[^a-zA-Z0-9.\-_]/g, '_'); // Sanitize email for filename

      let counter = 1;
      for (const photo of coverPhotos) {
        const safePhotoName = photo.name.replace(/[^a-zA-Z0-9.\-_]/g, '_'); // Sanitize file name
        const photoFileName = `${uploadDate}_${safeEmail}_${counter}_${safePhotoName}`;
        counter++;

        // Resize the image
        const resizedPhoto = await readAndCompressImage(photo, imageResizeConfig);

        const response = await fetch(`${SERVER_URL}/upload/signed-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            fileName: photoFileName,
            contentType: photo.type,
          }),
        });

        if (!response.ok) throw new Error('Failed to get signed upload URL for cover photo');
        const { signedUrl } = await response.json();

        const uploadResponse = await fetch(signedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': photo.type },
          body: resizedPhoto,
        });

        if (!uploadResponse.ok) throw new Error('Failed to upload cover photo');
        coverPhotoUrls.push(`uploads/${photoFileName}`);
      }

      setStatusMessage('Creating zip file...');
      setZippingProgress(0);

      // Step 2: Generate the zip file name
      const zipFileName = `${uploadDate}_${safeEmail}.zip`;

      // Step 3: Zip the files locally
      const zip = new JSZip();
      const totalFiles = files.length;
      let processedFiles = 0;

      files.forEach((file) => {
        zip.file(file.name, file);
        processedFiles++;
        setZippingProgress(Math.round((processedFiles / totalFiles) * 100)); // Update zipping progress
      });

      const zipBlob = await zip.generateAsync(
        { type: 'blob' },
        (metadata) => {
          setZippingProgress(Math.round(metadata.percent)); // Update zipping progress during compression
        }
      );

      setStatusMessage('Requesting signed upload URL for zip file...');
      setZippingProgress(100);

      // Step 4: Request a signed upload URL for the zip file
      const zipResponse = await fetch(`${SERVER_URL}/upload/signed-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          fileName: zipFileName,
          contentType: 'application/zip',
        }),
      });

      if (!zipResponse.ok) throw new Error('Failed to get signed upload URL for zip file');
      const { signedUrl: zipSignedUrl } = await zipResponse.json();

      setStatusMessage('Uploading zip file to storage...');
      setUploadProgress(0);

      // Step 5: Upload the zip file with progress tracking using XMLHttpRequest
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', zipSignedUrl, true);
        xhr.setRequestHeader('Content-Type', 'application/zip');

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(progress);
          }
        };

        xhr.onloadstart = () => {
          setUploadProgress(0); // Ensure progress starts at 0%
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            setUploadProgress(100); // Ensure progress ends at 100%
            resolve();
          } else {
            reject(new Error(`Failed to upload zip file: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => {
          reject(new Error('An error occurred during the upload.'));
        };

        xhr.send(zipBlob);
      });

      setStatusMessage('Saving upload details...');

      // Step 6: Notify the server about the uploaded files
      const saveResponse = await fetch(`${SERVER_URL}/upload/files`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          senderName,
          title,
          message, // Save the message as-is with line breaks
          zipFileName,
          coverPhotoUrls,
        }),
      });

      if (!saveResponse.ok) throw new Error('Failed to save upload details');
      const { token, expiration } = await saveResponse.json();

      if (token) {
        const downloadLink = `${window.location.origin}/download/${token}`;
        const formattedExpiration = new Date(expiration).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
        setStatusMessage(
          <>
            Files uploaded successfully. <br /><br />
            Download link: <br />
            <a
              href={downloadLink}
              target="_blank"
              rel="noopener noreferrer"
              className="download-link"
            >
              {downloadLink}
            </a>
            <br /><br />
            This link will expire on {formattedExpiration}.
          </>
        );
        setUploadCompleted(true); // Mark upload as completed
      } else {
        setStatusMessage('Upload completed, but no download link was provided.');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setStatusMessage('Error uploading files.');
    } finally {
      setUploading(false);
      setIsProcessing(false); // Hide spinner
    }
  };

  const formatFileSize = (size) => {
    if (size >= 1024 * 1024 * 1024) {
      return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    } else if (size >= 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    } else if (size >= 1024) {
      return `${(size / 1024).toFixed(2)} KB`;
    }
    return `${size} bytes`;
  };

  return (
    <>
      <div className="top-bar"> {/* Top bar for the title */}
        <h1 className="main-title">Clickz File Transfer</h1>
      </div>
      <div className="upload-container">
        <h1 className={uploadCompleted ? 'upload-completed-title' : ''}>
          {uploadCompleted ? 'Upload Completed' : 'Upload Your Files'}
        </h1>
        {step === 1 && (
          <div className="upload-form-step">
            <input
              type="email"
              placeholder="Your Email"
              value={email}
              onChange={handleEmailChange}
              className="upload-input-field"
            />
            <input
              type="text"
              placeholder="Your Name (optional)"
              value={senderName}
              onChange={handleSenderNameChange}
              className="upload-input-field"
            />
            <input
              type="text"
              placeholder="Title (optional)"
              value={title}
              onChange={handleTitleChange}
              className="upload-input-field"
            />
            <textarea
              placeholder="Personal Message (optional)"
              value={message}
              onChange={handleMessageChange}
              className="upload-textarea-field"
            />
            <label className="upload-label">Upload Files:</label>
            <input type="file" multiple onChange={handleFileChange} className="upload-file-input" />
            <div className="file-size-indicator">
              <p>
                {formatFileSize(totalFileSize)} of {formatFileSize(MAX_UPLOAD_SIZE)}
              </p>
              {exceedsLimit && (
                <p className="exceeds-limit-message" style={{ color: 'red', marginBottom: '5px' }}>
                  The total upload size exceeds the 20GB limit.
                </p>
              )}
              <div className="progress-bar-wrapper">
                <div
                  className="progress-bar"
                  style={{
                    width: `${(totalFileSize / MAX_UPLOAD_SIZE) * 100}%`,
                    backgroundColor: exceedsLimit ? 'red' : '#4caf50',
                  }}
                />
              </div>
            </div>
            <label className="upload-label">Upload Cover Photos (optional):</label>
            {statusMessage && statusMessage.includes('Only JPG files') && (
              <p className="exceeds-limit-message" style={{ color: 'red', marginBottom: '5px' }}>
                {statusMessage}
              </p>
            )}
            <input type="file" multiple onChange={handleCoverPhotoChange} className="upload-file-input" />
            <div className="upload-recaptcha">
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleCaptchaChange}
              />
            </div>
            <label className="terms-checkbox">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={handleTermsChange}
              />
              I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
            </label>
            <button
              onClick={sendOtp}
              className="upload-btn-primary"
              disabled={!termsAccepted} // Disable button if terms are not accepted
            >
              Send OTP
            </button>
          </div>
        )}
        {step === 2 && (
          <div className="upload-form-step">
            <input
              type="text"
              placeholder="Enter OTP"
              value={otp}
              onChange={handleOtpChange}
              className="upload-input-field"
            />
            <button onClick={verifyOtp} className="upload-btn-primary">
              Verify OTP
            </button>
          </div>
        )}
        {step === 3 && (
          <div className="upload-form-step">
            {uploadCompleted ? (
              <div className="success-animation">
                <div className="checkmark"></div>
              </div>
            ) : (
              <>
                {isProcessing && <div className="spinner"></div>} {/* Spinner */}
                {zippingProgress < 100 && (
                  <div className="progress-container">
                    <p>Zipping Progress</p>
                    <div className="progress-bar-wrapper">
                      <div
                        className="progress-bar"
                        style={{ width: `${zippingProgress}%` }}
                      />
                    </div>
                    <p>{zippingProgress}%</p>
                  </div>
                )}
                {zippingProgress === 100 && uploadProgress < 100 && (
                  <div className="progress-container">
                    <p>Uploading Progress</p>
                    <div className="progress-bar-wrapper">
                      <div
                        className="progress-bar"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p>{uploadProgress}%</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {statusMessage && <p className="upload-status-message">{statusMessage}</p>}

        {/* Branding */}
        <div className="upload-branding">
          <img src="/clickzLogo.png" alt="Clickz Logo" className="upload-branding-logo" />
          <p className="upload-branding-text">Powered by Clickz</p>
        </div>
      </div>
    </>
  );
};

export default Upload;
